import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { useAppContext } from 'providers/AppProvider';
import { TooltipComponent } from 'echarts/components';
import { PieChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { rgbaColor } from 'helpers/utils';
import { useEffect, useRef, useState } from 'react';
import { usePowerStationFilterStore } from '../stores/PowerStationFilterStore';
import { getMonitoringLabels } from '../Utils'
import dayjs from 'dayjs';

echarts.use([TooltipComponent, PieChart, CanvasRenderer]);


const MonitoringDeadlinesChart = () => {
	const { getThemeColor } = useAppContext();
	const chartRef = useRef(null);
	const [total, setTotal] = useState(1);
	const dataMonitoring = getMonitoringLabels();

	const activeAnomalyRecords = usePowerStationFilterStore((state) => state.activeAnomalyRecords);
	const monitoringFilter = usePowerStationFilterStore((state) => state.monitoringFilter);
	const filterActiveAnomalyRecordsByMonitoring = usePowerStationFilterStore((state) => state.filterActiveAnomalyRecordsByMonitoring);

	const setSeriesData = () => {
		let expired = 0;
		let near = 0;
		let farAway = 0;
		const now = Date.now();

		let anomalyRecordsMonitoring = activeAnomalyRecords.filter(x => x.nextMonitoring !== null);

		const anomalyRecordsMonitoringRecord = anomalyRecordsMonitoring.reduce((acc: Record<string, number>, anomalyRecordMonitoring) => {
			const key: string = anomalyRecordMonitoring.id;
			const nextMonitoring = anomalyRecordMonitoring.nextMonitoring ? dayjs(anomalyRecordMonitoring.nextMonitoring) : null;
			const date = nextMonitoring && nextMonitoring.isValid() ? nextMonitoring.toDate().getTime() : 0;

			if (!acc[key]) {
				acc[key] = date;
			} else if (date > acc[key]) {
				acc[key] = date;
			}
			return acc;
		}, {} as Record<string, number>);

		Object.values(anomalyRecordsMonitoringRecord).forEach(date => {
			const diffDays = (date - now) / (1000 * 60 * 60 * 24);
			if (diffDays < 0) {
				expired++;
			} else if (diffDays <= 30) {
				near++;
			} else {
				farAway++;
			}
		});

		return [
			{ value: expired, name: dataMonitoring[0] },
			{ value: near, name: dataMonitoring[1] },
			{ value: farAway, name: dataMonitoring[2] },
		];
	};

	const seriesData = setSeriesData();

	useEffect(() => {
		setTotal(activeAnomalyRecords.length || 1);
	}, [activeAnomalyRecords]);

	const getDefaultOptions = (getThemeColor: (name: string) => string) => {
		const selected: Record<string, boolean> = {};
		seriesData.forEach(item => {
			selected[item.name] = item.value > 0;
		});

		return {
			color: [
				'rgba(255, 0, 0, 0.7)',
				'rgba(255, 255, 0, 0.5)',
				'rgba(0, 255, 0, 0.5)',
			],
			// title: {
			//   text: 'Prazo de Monitoramento',
			//   left: 'center',
			//   top: '10%'
			// },
			legend: {
				data: dataMonitoring,
				bottom: '0px',
				left: '0px',
				orient: 'vertical',
				align: 'left',
				textStyle: {
					color: getThemeColor('gray-800'),
					fontSize: 12
				},
				selected: selected
			},
			toolbox: {
				show: monitoringFilter.length > 0,
				feature: {
					restore: {
						show: true,
						title: 'Limpar filtro'
					},
				}
			},
			series: [
				{
					type: 'pie',
					radius: '50%',
					center: ['50%', '40%'],
					label: {
						formatter: (params: { value: number; }) => {
							return ((params.value / total) * 100).toFixed(1) + '%';
						},
						color: getThemeColor('gray-700')
					},
					data: seriesData,
					emphasis: {
						itemStyle: {
							shadowBlur: 10,
							shadowOffsetX: 0,
							shadowColor: rgbaColor(getThemeColor('gray-600'), 0.5)
						}
					}
				}
			],
			tooltip: {
				trigger: 'item',
				padding: [7, 10],
				backgroundColor: getThemeColor('gray-100'),
				borderColor: getThemeColor('gray-300'),
				textStyle: { color: getThemeColor('gray-1100') },
				borderWidth: 1,
				transitionDuration: 0,
				axisPointer: {
					type: 'none'
				}
			}
		};
	};

	const handleClick = (params: any) => {
		filterActiveAnomalyRecordsByMonitoring([params.name]);
	}

	const handleLegendClick = (params: any) => {
		const selectedMap = params.selected;
		const activeLegends = Object.keys(selectedMap).filter(name => selectedMap[name]);
		filterActiveAnomalyRecordsByMonitoring(activeLegends);
	};

	const handleRestore = (params: any) => {
		filterActiveAnomalyRecordsByMonitoring([]);
	}

	return (
		<ReactEChartsCore
			echarts={echarts}
			option={getDefaultOptions(getThemeColor)}
			style={{ minHeight: '350px', width: '100%' }}
			ref={chartRef}
			onEvents={{
				click: handleClick,
				legendselectchanged: handleLegendClick,
				restore: handleRestore
			}}
		/>
	);
};

export default MonitoringDeadlinesChart;
